import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ICellRendererParams } from 'ag-grid-community';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from 'components/mui/Tooltip';

type FormattedTotals = {
  totalInCurrency?: string;
  totalInCompanyCurrency?: string;
  invoicesCount?: number;
};

type AmountRow = {
  underlings?: unknown[];
};

type AmountCellContext = {
  detailsIsExpanded: boolean;
  isPriorityErp: boolean;
};

type AmountCellRendererProps = Omit<
  ICellRendererParams<AmountRow, unknown, AmountCellContext>,
  'valueFormatted'
> & {
  valueFormatted: FormattedTotals;
};

const AmountCellRenderer = (props: AmountCellRendererProps) => {
  const { formatMessage } = useIntl();
  const { valueFormatted, context, data } = props;

  const { totalInCurrency, totalInCompanyCurrency, invoicesCount } = valueFormatted;
  const isParent = data?.underlings;
  const { detailsIsExpanded, isPriorityErp } = context;

  const tooltipContent = useMemo(() => {
    return (
      <div>
        <p>{totalInCompanyCurrency}</p>
        <p>{totalInCurrency}</p>
        {!isPriorityErp && (
          <p>
            {formatMessage(
              {
                id: 'company.dashboard.vendorExpenses.invoicesCount',
                defaultMessage: `${invoicesCount} invoices`,
              },
              { value: invoicesCount },
            )}
          </p>
        )}
      </div>
    );
  }, [totalInCurrency, totalInCompanyCurrency, invoicesCount, formatMessage, isPriorityErp]);

  return (
    <Tooltip t={tooltipContent}>
      <Box sx={{ textAlign: 'right', maxWidth: '100%' }}>
        <Typography variant="body2" noWrap>
          {isParent ? totalInCompanyCurrency : totalInCurrency}
        </Typography>
        {!isParent && detailsIsExpanded && (
          <Typography variant="body2" noWrap color="text.secondary" sx={{ fontSize: 12 }}>
            {totalInCompanyCurrency}
          </Typography>
        )}
        {detailsIsExpanded && (
          <Typography variant="body2" noWrap color="text.secondary" sx={{ fontSize: 12 }}>
            {formatMessage(
              {
                id: 'company.dashboard.vendorExpenses.invoicesCount',
                defaultMessage: `${invoicesCount} invoices`,
              },
              { value: invoicesCount },
            )}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default memo(AmountCellRenderer);
